import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';

import { uniq } from 'lodash';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { BlockTypeModel } from '../../../PageBuilder/models/block-type.model';

import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ChartDisplayElementModel } from '../../../Reports/models/chart-display-element.model';
import { ReportsService } from '../../../Reports/services/reports.service';
import { ChartSetupDialogComponent } from '../../../Reports/components/ChartSetupDialog/chart-setup-dialog.component';
import { ChartBlockComponent } from '../../../Reports/components/ChartBlock/chart-block.component';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { reportBlockTypesMap } from '../../../Reports/models/reports-block-types.model';
import { CoverageSummaryChartBlockComponent } from '../../../Reports/components/CoverageSummaryChartBlock/coverage-summary-chart-block.component';
import { TopTermChartBlockComponent } from '../../../Reports/components/TopTermChartBlock/top-term-chart-block.component';
import { DimensionsModel } from '../../../Misc/models/dimensions.model';

import { ChartSetupSearchService } from '../../services/chart-setup-search.service';
import { DashboardPageBuilderService } from '../../services/dashboard-page-builder.service';
import { dashboardChartOptions, reportChartOptions } from '../../models/chart-setup-option.model';
import { DashboardCoverageSummaryChartBlockComponent } from '../DashboardCoverageSummaryChartBlock/dashboard-coverage-summary-chart-block.component';
import { DashboardChartBlockComponent } from '../DashboardChartBlock/dashboard-chart-block.component';
import { DashboardTopTermChartBlockComponent } from '../DashboardTopTermChartBlock/dashboard-top-term-chart-block.component';
import { ReportDateRangeModel } from '../../../Reports/models/report-date-range.model';
import { dateQueryInput7DaysOption } from '../../../Forms/models/date-query-input-options.model';
import { TopPostChartBlockComponent } from '../../../Reports/components/TopPostChartBlock/top-post-chart-block.component';
import { DashboardTopPostsChartBlockComponent } from '../DashboardTopPostsChartBlock/dashboard-top-posts-chart-block.component';
import { TopMentionsChartBlockComponent } from '@Common/Reports/components/TopMentions/TopMentionsChartBlock/top-mentions-chart-block.component';
import { DashboardTopMentionsChartBlockComponent } from '../DashboardTopMentionsChartBlock/dashboard-top-mentions-chart-block.component';

@Component({
    selector: 'agpr-chart-settings-dialog',
    templateUrl: '../../../Reports/components/ChartSetupDialog/chart-setup-dialog.component.html',
    styleUrls: ['../../../Reports/components/ChartSetupDialog/chart-setup-dialog.component.scss'],
    providers: [
        {
            provide: 'SortOrderServiceInterface',
            useExisting: ChartSetupSearchService
        },
        {
            provide: AbstractPageBuilderService,
            useExisting: DashboardPageBuilderService
        },
        {
            provide: FilterService,
            useExisting: ChartSetupSearchService
        }
    ],
    standalone: false
})

export class ChartSettingsDialogComponent extends ChartSetupDialogComponent implements OnInit, OnDestroy {

    constructor(
        public coreDialogService: CoreDialogService,
        protected renderer: Renderer2,
        public pageBuilderService: DashboardPageBuilderService,
        public localizationService: LocalizationService,
        public reportsService: ReportsService,
        public chartSetupSearchService: ChartSetupSearchService,
        protected currentUserStateService: CurrentUserStateService,
        public router: Router,
    ) {
        super(
            coreDialogService,
            renderer,
            pageBuilderService,
            localizationService,
            reportsService,
            chartSetupSearchService,
            currentUserStateService,
            router,
        );

        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (permissions) {
                    const hasTargeting = permissions.includes(userPermissionsEnum.advancedTargeting);
                    const hasMonitoring = permissions.includes(userPermissionsEnum.advancedMonitoring);
                    const hasSocial = permissions.includes(userPermissionsEnum.socialIntelligence);

                    const targetingCharts = dashboardChartOptions
                        .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedTargeting));

                    const monitoringCharts = dashboardChartOptions
                        .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedMonitoring));

                    const reportCharts = reportChartOptions
                        .filter(chart => chart.permissions.includes(userPermissionsEnum.advancedMonitoring));

                    const socialCharts = reportChartOptions
                        .filter(chart => chart.permissions.includes(userPermissionsEnum.socialIntelligence));

                    let defaultDashboardOptions = [];

                    if (hasMonitoring) {
                        defaultDashboardOptions = [...defaultDashboardOptions, ...monitoringCharts, ...reportCharts];
                    }
                    if (hasTargeting) {
                        defaultDashboardOptions = [...defaultDashboardOptions, ...targetingCharts];
                    }
                    if (hasSocial) {
                        defaultDashboardOptions = [...defaultDashboardOptions, ...socialCharts];
                    }

                    defaultDashboardOptions = uniq(defaultDashboardOptions, 'blockType.key');

                    this.chartSetupSearchService.setDefaultChartOptions(defaultDashboardOptions);
                    this.chartSetupSearchService.setResults(defaultDashboardOptions);
                    this.chartSetupSearchService.loadResults();
                }
            }),
        );
    }

    // no validation needed for the dashboard page
    validateChart(): void {
        if (!this.blockClone || !this.blockClone.type || !this.blockClone.displayElement) {
            this.chartIsValid = false;
            return;
        }
        if (reportBlockTypesMap.get(this.blockClone.type?.key as ReportBlockTypeEnum)) {
            super.validateChart();
        } else {
            this.chartIsValid = true;
        }

    }

    setBlock(type: BlockTypeModel) {
        // must clone it first, or it will change it on the reports page as well
        type = type.clone();
        type.isDashboard = true;

        switch (type.componentType) {
            case ChartBlockComponent:
                type.componentType = DashboardChartBlockComponent;
                break;
            case TopTermChartBlockComponent:
                type.componentType = DashboardTopTermChartBlockComponent;
                break;
            case CoverageSummaryChartBlockComponent:
                type.componentType = DashboardCoverageSummaryChartBlockComponent;
                break;
            case TopPostChartBlockComponent:
                type.componentType = DashboardTopPostsChartBlockComponent;
                break;
            case TopMentionsChartBlockComponent:
                type.componentType = DashboardTopMentionsChartBlockComponent;
                break;
            default:
                break;
        }

        super.setBlock(type);

        const chartDisplayElement = this.blockClone.displayElement as ChartDisplayElementModel;

        if (type.key === ReportBlockTypeEnum.RECENT_SURGE_ALERTS) {
            chartDisplayElement.numOfItems = 4;
            chartDisplayElement.dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput7DaysOption.value.query);
        }

        // HACK for now -> set all new dashboard charts to be 2x2 grid
        const dimensions = new DimensionsModel(2, 2);

        chartDisplayElement.size = dimensions;
        this.blockClone.rows = dimensions.height;
        this.blockClone.cols = dimensions.width;

        this.setBlockSize();

        this.pageBuilderService.selectBlock(this.blockClone);
    }

    /**
     * Sets the preview block size so it will display it's contents at the right size
     */
    setBlockSize(): void {
        if (!this.blockClone?.type?.key) {
            return;
        }

        const gridSettings = this.pageBuilderService.gridSettings;
        const cols = 2;
        const rows = 2;

        this.blockWidth = cols * gridSettings.fixedColWidth + (cols - 1) * gridSettings.margin;
        this.blockHeight = rows * gridSettings.fixedRowHeight + (rows - 1) * gridSettings.margin;
    }

    /**
    * Adds the new chart to page builder
    */
    addChart(): void {
        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        displayElement.series.forEach(_series => {
            // mapping numOfItems and termType to series, which will be used to construct BE request
            _series.termType = displayElement.termType;
            _series.numOfItems = displayElement.numOfItems;
        });

        this.pageBuilderService.addBlock(this.blockClone);

        this.doHide();
    }

    /**
    * Saves the changes back to the chart
    */
    updateChart(): void {
        // @HACK Fixes a bug that if you click "update chart" while the date filter calendar is open, then the dates you picked will not be selected
        if (this.isDatePickerOpen()) {
            requestAnimationFrame(() => {
                this.updateChart();
            });
            return;
        }

        // so here clear series data so that old data would not be emitted to chart when updating chart
        const displayElement = this.blockClone.displayElement as ChartDisplayElementModel;
        displayElement.series.forEach(_series => {
            // BehaviorSubject is used for dataProvider
            // so here clear series data so that old data would not be emitted to chart when updating chart
            _series.clearData();

            // mapping numOfItems and termType to series, which will be used to construct BE request
            _series.termType = displayElement.termType;
            _series.numOfItems = displayElement.numOfItems;
        });

        // need to replace to make it handle the resize correctly
        this.pageBuilderService.replaceBlock(this.originBlock, this.blockClone);

        this.doHide();
    }

    doHide(): void {
        this.pageBuilderService.deselectBlock(true);
        this.closeDialog(true);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.chartSetupSearchService.clearFilters();
    }
}
